import { animate, state, style, transition, trigger } from '@angular/animations';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    computed,
    ElementRef,
    inject,
    input,
    OnDestroy,
    OnInit,
    signal,
    viewChild
} from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';

import { BaseComponent } from '../../../core/abstractions/base.component';
import { Tools } from '../../../core/utils/tools';
import { NotificationModel } from '../../../models/notification.model';
import { AuthService } from '../../../services/auth.service';
import { NotificationsService } from '../../../services/notifications.service';
import { MetaResource } from '../../../services/resources/meta.resource';
import { TitleService } from '../../../services/title.service';
import { TranslationService } from '../../../services/translation.service';

@Component({
    selector: 'sl-topbar',
    templateUrl: './topbar.component.html',
    styleUrls: ['./topbar.component.scss'],
    animations: [
        trigger('childAnimation', [
            state(
                'open',
                style({
                    width: '100%',
                    opacity: 1
                })
            ),
            state(
                'closed',
                style({
                    width: '0',
                    opacity: 0
                })
            ),
            transition('* => *', [animate('0.1s')])
        ])
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TopbarComponent extends BaseComponent implements OnInit, OnDestroy, AfterViewInit {
    searchInput = viewChild.required('searchTerm', { read: ElementRef<HTMLInputElement> });
    showLogo = input(false);
    shouldHideSearch = input(false);
    isNetvote = input(false);

    currentLogo: string;
    areLanguagesLoaded = false;
    notifications: NotificationModel[] = [];
    isLoadingNotifications = true;
    isNotificationsOverlayOpenChange: Observable<boolean>;
    readonly isSearchbarOpen = signal(false);
    readonly currentToolbarTitle = computed(() => this.titleService.currentToolbarTitle());
    readonly toolbarColor = computed(() => (this.isNetvote() ? undefined : 'primary'));

    private readonly router = inject(Router);
    private readonly authService = inject(AuthService);
    private readonly metaResource = inject(MetaResource);
    private readonly translationService = inject(TranslationService);
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly notificationsService = inject(NotificationsService);
    private readonly titleService = inject(TitleService);

    ngOnInit(): void {
        if (this.translationService.allowedLanguages.length > 0) {
            this.areLanguagesLoaded = true;
        } else {
            const langsLoadedSub = this.translationService.allowedLanguagesLoadedSub.subscribe(() => {
                this.areLanguagesLoaded = true;
                this.changeDetectorRef.detectChanges();
            });
            this.addSubscriptions(langsLoadedSub);
        }

        this.metaResource.getLogo().subscribe(resp => (this.currentLogo = Tools.Image.getImgSrcForBase64(resp.value)));
    }

    ngAfterViewInit(): void {
        if (!this.isNetvote()) {
            this.isNotificationsOverlayOpenChange = this.notificationsService.isOverlayOpenChange;
            this.notificationsService.getNotifications().subscribe(notifications => {
                this.notifications = notifications;
                this.isLoadingNotifications = false;
            });
        }
    }

    goToMyProfile(): void {
        this.router.navigate(['/account/my-profile']).then();
    }

    logout(): void {
        this.authService.logout(true);
    }

    toggleSearchBar(): void {
        if (this.isSearchbarOpen()) {
            this.searchInput().nativeElement.value = '';
            this.isSearchbarOpen.set(false);
        } else {
            this.isSearchbarOpen.set(true);
            setTimeout(() => this.searchInput().nativeElement.focus(), 100);
        }
    }

    search(searchTerm: any): void {
        if (!searchTerm) {
            return;
        }

        this.router.navigate(['/search', searchTerm]).then();
    }

    toggleNotificationsOverlay(isOpen?: boolean): void {
        this.notificationsService.toggleOverlay(isOpen);
    }
}
